import _set from 'lodash/set';
import { CustomsUserType } from '@packlink/packlink-sdk';
import { transformCountryToCountryOption } from '@packlink/address-selectors';
import { translateSelectOption } from '../utils/select.js';
import { getCustomsFormValidation } from './validations/customsFormValidations.js';
import * as yup from 'yup';
import { reasonsToExportOptions } from './ReasonsToExport.js';
import { otherTariffNumberOption, OTHERS_CATEGORY } from './CustomsInvoiceItemTypes.js';
import { userTaxTypeOptions } from './TaxFormTypes.js';

function findAutocompleteOption(value, options) {
  return value ? options.find(option => option.value === value) : undefined;
}
function transformCustomsInvoiceToForm(_ref) {
  let {
    currency,
    t,
    status,
    validTariffNumbers
  } = _ref;
  return customsInvoice => {
    const reasonsToExport = findAutocompleteOption(customsInvoice.reasonForExport, reasonsToExportOptions);
    return {
      id: customsInvoice.id,
      // Status comes from shipment
      status,
      // Currency comes also in backend data, and it should be the same
      // but just in case the frontend one is used
      currency,
      inventoryOfContents: customsInvoice.inventory.map(item => transformCustomsInventoryToForm({
        item,
        validTariffNumbers,
        t
      })),
      sender: transformCustomsClientToForm(customsInvoice.sender, customsInvoice.senderTaxInformation, t),
      receiver: transformCustomsClientToForm(customsInvoice.receiver, customsInvoice.receiverTaxInformation, t),
      reasonsToExport: reasonsToExport ? translateSelectOption(reasonsToExport, t) : undefined,
      invoiceNumber: customsInvoice.invoiceNumber,
      customsTerms: false,
      authorised: false
    };
  };
}
function getNewCustomsInventoryItem() {
  return {
    description: '',
    tariffNumber: undefined,
    countryOfOrigin: undefined,
    numberOfItems: undefined,
    value: undefined,
    weight: undefined,
    category: undefined
  };
}
function transformCustomsInventoryToForm(_ref2) {
  var _item$description;
  let {
    item,
    validTariffNumbers,
    t
  } = _ref2;
  const matchedCategory = validTariffNumbers.find(option => {
    var _item$tariffNumber;
    return ((_item$tariffNumber = item.tariffNumber) === null || _item$tariffNumber === void 0 ? void 0 : _item$tariffNumber.toString()) === option.tariff_number;
  }) || otherTariffNumberOption;
  const isOtherCategory = matchedCategory.tariff_number === OTHERS_CATEGORY;
  return {
    // This value is populated with a valid Country by CountrySelector later
    // TODO: This should not be needed after this CountrySelector refactor https://packlink.atlassian.net/browse/PS-298
    countryOfOrigin: item.countryOfOrigin ? transformCountryToCountryOption({
      alpha2Code: item.countryOfOrigin,
      name: ''
    }) : undefined,
    description: (_item$description = item.description) !== null && _item$description !== void 0 ? _item$description : '',
    tariffNumber: item.tariffNumber && isOtherCategory ? Number(item.tariffNumber) : undefined,
    numberOfItems: item.quantity || undefined,
    value: item.value || undefined,
    weight: item.weight || undefined,
    category: transformTariffNumberToAutocompleteOption(matchedCategory, t)
  };
}
function transformTariffNumberToAutocompleteOption(_ref3, t) {
  let {
    tariff_number,
    description
  } = _ref3;
  return translateSelectOption({
    value: tariff_number,
    label: description
  }, t);
}
function transformCustomsClientToForm(client, taxInformation, t) {
  var _client$company, _taxInformation$eori, _taxInformation$taxId, _taxInformation$vatNu;
  const clientVatStatus = findAutocompleteOption(taxInformation === null || taxInformation === void 0 ? void 0 : taxInformation.userType, userTaxTypeOptions);
  return {
    vatStatus: clientVatStatus ? translateSelectOption(clientVatStatus, t) : undefined,
    company: (_client$company = client === null || client === void 0 ? void 0 : client.company) !== null && _client$company !== void 0 ? _client$company : '',
    eori: (_taxInformation$eori = taxInformation === null || taxInformation === void 0 ? void 0 : taxInformation.eori) !== null && _taxInformation$eori !== void 0 ? _taxInformation$eori : '',
    taxId: (_taxInformation$taxId = taxInformation === null || taxInformation === void 0 ? void 0 : taxInformation.taxId) !== null && _taxInformation$taxId !== void 0 ? _taxInformation$taxId : '',
    vatNumber: (_taxInformation$vatNu = taxInformation === null || taxInformation === void 0 ? void 0 : taxInformation.vatNumber) !== null && _taxInformation$vatNu !== void 0 ? _taxInformation$vatNu : ''
  };
}
function transformFormToCustomsInvoice(_ref4, _ref5) {
  let {
    id,
    currency,
    inventoryOfContents = [],
    sender: senderTaxInformation,
    receiver: receiverTaxInformation,
    reasonsToExport,
    invoiceNumber
  } = _ref4;
  let {
    sender,
    receiver,
    shipmentDetails,
    signature
  } = _ref5;
  const senderFormData = transformUserFormToCustomsInvoiceUser({
    address: sender,
    taxInformation: senderTaxInformation
  });
  const receiverFormData = transformUserFormToCustomsInvoiceUser({
    address: receiver,
    taxInformation: receiverTaxInformation
  });
  return {
    id: id !== null && id !== void 0 ? id : '',
    currency: currency !== null && currency !== void 0 ? currency : '',
    inventory: inventoryOfContents.map(transformTaxFormInventoryItemToSdk),
    sender: senderFormData.address,
    senderTaxInformation: senderFormData.taxInformation,
    receiver: receiverFormData.address,
    receiverTaxInformation: receiverFormData.taxInformation,
    shipmentDetails: {
      ...shipmentDetails
    },
    reasonForExport: reasonsToExport === null || reasonsToExport === void 0 ? void 0 : reasonsToExport.value,
    invoiceNumber: invoiceNumber,
    signature
  };
}
function transformUserFormToCustomsInvoiceUser(_ref6) {
  let {
    address,
    taxInformation
  } = _ref6;
  const parsedAddress = address.clone();
  parsedAddress.company = taxInformation === null || taxInformation === void 0 ? void 0 : taxInformation.company;
  return {
    address: parsedAddress,
    taxInformation: transformTaxFormToSdk(taxInformation)
  };
}
function transformClientToTaxForm(client, t) {
  const userType = client.isBusinessClient() ? CustomsUserType.COMPANY : CustomsUserType.PRIVATE_PERSON;
  const vatStatus = findAutocompleteOption(userType, userTaxTypeOptions);
  return {
    vatStatus: vatStatus ? translateSelectOption(vatStatus, t) : undefined,
    company: client.isBusinessClient() ? client.companyName : '',
    vatNumber: client.isBusinessClient() ? client.taxId : '',
    taxId: '',
    eori: ''
  };
}
function transformTaxFormInventoryItemToSdk(item) {
  var _item$category, _item$category2, _item$countryOfOrigin, _item$countryOfOrigin2, _item$description2, _item$numberOfItems, _item$value, _item$weight;
  const tariffNumber = ((_item$category = item.category) === null || _item$category === void 0 ? void 0 : _item$category.value) === OTHERS_CATEGORY ? item.tariffNumber : (_item$category2 = item.category) === null || _item$category2 === void 0 ? void 0 : _item$category2.value;
  return {
    countryOfOrigin: (_item$countryOfOrigin = (_item$countryOfOrigin2 = item.countryOfOrigin) === null || _item$countryOfOrigin2 === void 0 ? void 0 : _item$countryOfOrigin2.value) !== null && _item$countryOfOrigin !== void 0 ? _item$countryOfOrigin : '',
    description: (_item$description2 = item.description) !== null && _item$description2 !== void 0 ? _item$description2 : '',
    quantity: (_item$numberOfItems = item.numberOfItems) !== null && _item$numberOfItems !== void 0 ? _item$numberOfItems : 0,
    tariffNumber: (tariffNumber !== null && tariffNumber !== void 0 ? tariffNumber : '').toString(),
    value: (_item$value = item.value) !== null && _item$value !== void 0 ? _item$value : 0,
    weight: (_item$weight = item.weight) !== null && _item$weight !== void 0 ? _item$weight : 0
  };
}
function transformTaxFormToSdk(form) {
  var _form$vatStatus;
  if (!form) {
    return undefined;
  }
  return {
    eori: form.eori,
    taxId: form.taxId,
    vatNumber: form.vatNumber,
    userType: (_form$vatStatus = form.vatStatus) === null || _form$vatStatus === void 0 ? void 0 : _form$vatStatus.value
  };
}
function cloneFormData(form) {
  return {
    ...form,
    sender: {
      ...form.sender
    },
    receiver: {
      ...form.receiver
    },
    inventoryOfContents: form.inventoryOfContents.map(item => ({
      ...item,
      countryOfOrigin: {
        ...item.countryOfOrigin
      }
    }))
  };
}
function transformFormToDraftCustomsInvoice(form, _ref7, context) {
  let {
    sender,
    receiver,
    shipmentDetails,
    signature
  } = _ref7;
  const {
    id,
    currency,
    inventoryOfContents = [],
    sender: senderTaxInformation,
    receiver: receiverTaxInformation,
    reasonsToExport,
    invoiceNumber
  } = omitInvalidCustomsFormData(form, context);
  const senderFormData = transformUserFormToCustomsInvoiceUser({
    address: sender,
    taxInformation: senderTaxInformation
  });
  const receiverFormData = transformUserFormToCustomsInvoiceUser({
    address: receiver,
    taxInformation: receiverTaxInformation
  });
  return {
    id: id !== null && id !== void 0 ? id : '',
    currency: currency !== null && currency !== void 0 ? currency : '',
    inventory: inventoryOfContents.map(transformDraftTaxFormInventoryItemToSdk),
    sender: senderFormData.address,
    senderTaxInformation: senderFormData.taxInformation,
    receiver: receiverFormData.address,
    receiverTaxInformation: receiverFormData.taxInformation,
    shipmentDetails: {
      ...shipmentDetails
    },
    reasonForExport: reasonsToExport === null || reasonsToExport === void 0 ? void 0 : reasonsToExport.value,
    invoiceNumber: invoiceNumber,
    signature
  };
}

/**
 * Validate the customs form and remove invalid data
 * Relevant when saving a draft customs form
 */
function omitInvalidCustomsFormData(form, context) {
  // t function is not relevant for filtering
  const dummyT = s => s;
  const formValidationSchema = yup.object().shape(getCustomsFormValidation(dummyT, context));
  try {
    formValidationSchema.validateSync(form, {
      abortEarly: false,
      context
    });
    return form;
  } catch (yupErrors) {
    const invalidParams = yupErrors.inner.map(_ref8 => {
      let {
        path
      } = _ref8;
      return path;
    });

    // Watch out! We clone the form since "lodash/set" mutates data
    const cleanForm = cloneFormData(form);
    invalidParams.forEach(path => _set(cleanForm, path, undefined));
    return cleanForm;
  }
}
function transformDraftTaxFormInventoryItemToSdk(_ref9) {
  let {
    countryOfOrigin,
    description,
    numberOfItems,
    category,
    tariffNumber,
    value,
    weight
  } = _ref9;
  return {
    countryOfOrigin: countryOfOrigin === null || countryOfOrigin === void 0 ? void 0 : countryOfOrigin.value,
    description: description,
    quantity: numberOfItems,
    tariffNumber: (category === null || category === void 0 ? void 0 : category.value) === OTHERS_CATEGORY ? tariffNumber === null || tariffNumber === void 0 ? void 0 : tariffNumber.toString() : category === null || category === void 0 ? void 0 : category.value,
    value: value,
    weight: weight
  };
}
function getNewCustomsInvoiceItem(_ref10) {
  let {
    defaultCustomsCategory,
    t
  } = _ref10;
  return {
    description: '',
    tariffNumber: undefined,
    countryOfOrigin: undefined,
    numberOfItems: undefined,
    value: undefined,
    weight: undefined,
    category: defaultCustomsCategory ? transformTariffNumberToAutocompleteOption(defaultCustomsCategory, t) : undefined
  };
}

export { cloneFormData, findAutocompleteOption, getNewCustomsInventoryItem, getNewCustomsInvoiceItem, transformClientToTaxForm, transformCustomsClientToForm, transformCustomsInventoryToForm, transformCustomsInvoiceToForm, transformFormToCustomsInvoice, transformFormToDraftCustomsInvoice, transformTariffNumberToAutocompleteOption, transformTaxFormInventoryItemToSdk };
//# sourceMappingURL=dataTransformation.js.map
